import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { QRCodeSVG } from 'qrcode.react';

import { addToFavorites, removeFromFavorites } from '../../../api/favoritecall';
import { trackButtonClick } from '../../../utils/AnalyticsUtils';

import heartIcon from '../../../assets/icons/heart.svg';
import activeHeartIcon from '../../../assets/icons/bottomBar/active_heart.svg';
import shareIcon from '../../../assets/icons/share.svg';
import chatIcon from '../../../assets/icons/chat.svg';
import markerIcon from '../../../assets/icons/marker.svg';
import calendarIcon from '../../../assets/icons/calendar.svg';
import leftArrow from '../../../assets/icons/leftarrow.svg';
import rightArrow from '../../../assets/icons/rightarrow.svg';
import userDefIcon from '../../images/def_user_icon.svg';
import placeholderImage from '../../../assets/images/noimage.png';

const APP_STORE_URL = 'https://apps.apple.com/kz/app/agroland/id6743326709';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.agroland.app&hl=en';

/* ─── Утилиталар ─────────────────────────────────────────────── */
function fmtPrice(price) {
    if (!price && price !== 0) return '—';
    return new Intl.NumberFormat('kk-KZ').format(price) + ' ₸';
}

/* ─── Суреттер галереясы ─────────────────────────────────────── */
function ImageGallery({ images = [] }) {
    const [idx, setIdx] = useState(0);
    const touchX = useRef(0);
    const list = images.length > 0 ? images : [null];
    const prev = () => setIdx((i) => (i - 1 + list.length) % list.length);
    const next = () => setIdx((i) => (i + 1) % list.length);

    return (
        <div className="w-full rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm">
            <div
                className="relative"
                onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                    const dx = touchX.current - e.changedTouches[0].clientX;
                    if (dx > 50) next(); else if (dx < -50) prev();
                }}
            >
                <img
                    src={list[idx] || placeholderImage}
                    alt={`photo-${idx + 1}`}
                    className="w-full object-cover"
                    style={{ height: '420px' }}
                    onError={(e) => { e.target.src = placeholderImage; }}
                />
                {list.length > 1 && (
                    <span className="absolute top-3 right-3 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {idx + 1} / {list.length}
                    </span>
                )}
                {list.length > 1 && (
                    <>
                        <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white">
                            <img src={leftArrow} alt="prev" className="w-4 h-4" />
                        </button>
                        <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white">
                            <img src={rightArrow} alt="next" className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>
            {list.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                    {list.map((img, i) => (
                        <button key={i} onClick={() => setIdx(i)} className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === idx ? 'border-[#147F26]' : 'border-transparent'}`}>
                            <img src={img || placeholderImage} alt={`thumb-${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = placeholderImage; }} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Жұлдызшалар ────────────────────────────────────────────── */
function Stars({ value = 5, size = 'sm' }) {
    const full = Math.round(parseFloat(value) || 5);
    const cls = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <svg key={i} className={`${cls} ${i <= full ? 'text-yellow-400' : 'text-gray-200'}`} viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                </svg>
            ))}
        </div>
    );
}

/* ─── Тапсырыс модалі ────────────────────────────────────────── */
function OrderModal({ seller, measurementUnit, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
            <div className="bg-white rounded-3xl w-full max-w-sm p-7 flex flex-col items-center gap-5 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center border-none cursor-pointer">
                    <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Жеткізуші ақпараты */}
                {seller && (
                    <div className="w-full flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3">
                        <img src={seller.avatar_url || userDefIcon} alt="seller" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                        <div className="min-w-0">
                            <p className="font-semibold text-sm text-gray-900 m-0 truncate">{seller.name}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                                <Stars value={seller.rating} />
                                <span className="text-xs text-gray-500">{String(parseFloat(seller.rating) || 5).slice(0, 3)}</span>
                            </div>
                        </div>
                        <div className="ml-auto text-right flex-shrink-0">
                            <p className="text-base font-bold text-[#147F26] m-0">{fmtPrice(seller.price)}</p>
                            {measurementUnit && <p className="text-xs text-gray-400 m-0">/ {measurementUnit}</p>}
                        </div>
                    </div>
                )}

                <div className="text-center">
                    <h3 className="text-lg font-bold text-[#147F26] m-0">{t('orderViaAppOnly')}</h3>
                    <p className="text-sm text-gray-500 mt-2 m-0 leading-relaxed">{t('orderViaAppDesc')}</p>
                </div>

                <div className="border-2 border-dashed border-[#7AB30E] rounded-2xl p-4 flex flex-col items-center gap-2 w-full">
                    <QRCodeSVG value="https://agroland.kz" size={150} />
                    <p className="text-xs text-gray-400 m-0">{t('scanQr')}</p>
                </div>

                <div className="flex flex-col gap-2.5 w-full">
                    <button onClick={() => window.open(APP_STORE_URL, '_blank')} className="w-full flex items-center justify-center gap-2 h-11 bg-black text-white rounded-xl font-semibold text-sm hover:bg-gray-900 border-none cursor-pointer">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/App_Store_%28iOS%29.svg/960px-App_Store_%28iOS%29.svg.png" alt="App Store" className="w-5 h-5 object-contain" />
                        App Store
                    </button>
                    <button onClick={() => window.open(PLAY_STORE_URL, '_blank')} className="w-full flex items-center justify-center gap-2 h-11 bg-[#147F26] text-white rounded-xl font-semibold text-sm hover:bg-[#0f6820] border-none cursor-pointer">
                        <img src="https://avatanplus.com/files/resources/original/5a8047a98f64c1618517ee73.png" alt="Google Play" className="w-5 h-5 object-contain" />
                        Google Play
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Жеткізуші жолы (sellers тізімінде) ────────────────────── */
function SellerRow({ seller, measurementUnit, onOrder, onMessage }) {
    const location = [seller.location?.province, seller.location?.locality].filter(Boolean).join(', ');

    return (
        <div className={`bg-white rounded-2xl border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 transition-shadow hover:shadow-md ${seller.in_stock === false ? 'opacity-60' : 'border-gray-100'}`}>

            {/* Сол: аватар + аты */}
            <div className="flex items-center gap-3 md:w-[220px] flex-shrink-0">
                <img src={seller.avatar_url || userDefIcon} alt={seller.name} className="w-12 h-12 rounded-full object-cover border border-gray-100 flex-shrink-0" />
                <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm m-0 leading-tight">{seller.name}</p>
                    <div className="flex items-center gap-1 mt-1">
                        <Stars value={seller.rating} />
                        <span className="text-xs text-gray-500 ml-0.5">{String(parseFloat(seller.rating) || 5).slice(0, 3)}</span>
                        <span className="text-xs text-gray-400">({seller.reviews || 0})</span>
                    </div>
                    {location && (
                        <div className="flex items-center gap-1 mt-1">
                            <img src={markerIcon} alt="" className="w-3 h-3 opacity-40 flex-shrink-0" />
                            <span className="text-xs text-gray-400 truncate">{location}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Орта: баға + жеткізу + мин. тапсырыс */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 flex-1">
                {/* Баға */}
                <div className="flex flex-col">
                    <span className="text-xs text-gray-400">{t('totalPrice') || 'Баға'}</span>
                    <span className="text-xl font-extrabold text-[#147F26] leading-tight">
                        {fmtPrice(seller.price)}
                    </span>
                    {measurementUnit && <span className="text-xs text-gray-400">/ {measurementUnit}</span>}
                </div>

                {/* Жеткізу */}
                {seller.delivery_time && (
                    <div className="flex flex-col justify-center">
                        <span className="text-xs text-gray-400">{t('deliveryTime') || 'Жеткізу'}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0M1 1h4l2.68 13.39a2 2 0 001.98 1.61h9.72a2 2 0 001.97-1.69L23 6H6" />
                            </svg>
                            <span className="text-sm font-semibold text-gray-800">{seller.delivery_time}</span>
                        </div>
                    </div>
                )}

                {/* Ең аз тапсырыс */}
                {seller.min_order && (
                    <div className="flex flex-col justify-center">
                        <span className="text-xs text-gray-400">{t('minOrder') || 'Ең аз'}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <span className="text-sm font-semibold text-gray-800">{seller.min_order}</span>
                        </div>
                    </div>
                )}

                {/* Қор */}
                <div className="flex flex-col justify-center">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-auto ${seller.in_stock === false ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                        {seller.in_stock === false ? (t('outOfStock') || 'Жоқ') : (t('inStock') || 'Бар')}
                    </span>
                </div>
            </div>

            {/* Оң: батырмалар */}
            <div className="flex flex-row md:flex-col gap-2 md:w-[140px] flex-shrink-0">
                <button
                    onClick={() => onOrder(seller)}
                    disabled={seller.in_stock === false}
                    className="flex-1 md:flex-none h-10 bg-[#147F26] hover:bg-[#0f6820] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl flex items-center justify-center gap-1.5 transition-colors border-none cursor-pointer"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {t('placeOrder') || 'Тапсырыс'}
                </button>
                <button
                    onClick={() => onMessage(seller)}
                    className="flex-1 md:flex-none h-10 border border-[#147F26] text-[#147F26] hover:bg-[#147F26]/5 font-semibold text-sm rounded-xl flex items-center justify-center gap-1.5 transition-colors bg-transparent cursor-pointer"
                >
                    <img src={chatIcon} alt="" className="w-4 h-4 flex-shrink-0" />
                    {t('message') || 'Хабарлама'}
                </button>
            </div>
        </div>
    );
}

/* ─── Негізгі компонент ──────────────────────────────────────── */
function DealerPage({ item, isMobile }) {
    const navigate = useNavigate();
    const [isFavorite, setIsFavorite] = useState(item.is_favorite || false);
    const [orderSeller, setOrderSeller] = useState(null); // модальда қай жеткізуші
    const [activeTab, setActiveTab] = useState('description');
    const sellersRef = useRef(null);

    useEffect(() => { setIsFavorite(item.is_favorite || false); }, [item.is_favorite]);

    const handleFavoriteToggle = async (e) => {
        e.stopPropagation();
        if (!localStorage.getItem('access_token')) { navigate('/sign'); return; }
        try {
            if (isFavorite) {
                await removeFromFavorites(item.id);
                trackButtonClick('remove_favorite', { announcement_id: item.id });
                setIsFavorite(false);
            } else {
                await addToFavorites(item.id);
                trackButtonClick('add_favorite', { announcement_id: item.id });
                setIsFavorite(true);
            }
        } catch { setIsFavorite((f) => !f); }
    };

    const handleShare = async () => {
        try {
            if (navigator.share) await navigator.share({ title: item.title, url: window.location.href });
            else await navigator.clipboard?.writeText(window.location.href);
            trackButtonClick('share_content', { announcement_id: item.id });
        } catch { /* ignored */ }
    };

    const handleMessageToSeller = (seller) => {
        navigate('/profile#messages', {
            state: { user_id: seller.id, announcement_id: item.id }
        });
        trackButtonClick('send_message', { announcement_id: item.id });
    };

    const scrollToSellers = () => {
        sellersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const sellers = Array.isArray(item.sellers) ? item.sellers : [];
    const inStockSellers = sellers.filter((s) => s.in_stock !== false);
    const minPrice = inStockSellers.length > 0 ? Math.min(...inStockSellers.map((s) => s.price || Infinity)) : null;
    const ratingNum = parseFloat(item.rating) || 5;
    const formattedDate = new Date(item.created_at).toLocaleDateString('kk-KZ', { year: 'numeric', month: 'long', day: 'numeric' });

    const tabs = [
        { key: 'description', label: t('description') || 'Сипаттама' },
        { key: 'specs', label: t('specifications') || 'Сипаттамалар' },
        { key: 'reviews', label: t('feedbacks') || 'Пікірлер' },
    ];

    return (
        <div className="px-4 md:px-10 lg:px-20 xl:px-32 py-6 pb-32 md:pb-10 max-w-[1400px] mx-auto">

            {/* ── Hero: 2 баған ───────────────────────────────── */}
            <div className="flex flex-col lg:flex-row gap-8">

                {/* Сол жақ — суреттер */}
                <div className="w-full lg:w-[52%] flex-shrink-0">
                    <ImageGallery images={Array.isArray(item.image_urls) && item.image_urls.length > 0 ? item.image_urls : []} />
                </div>

                {/* Оң жақ — негізгі ақпарат */}
                <div className="w-full lg:w-[48%] flex flex-col gap-4">

                    {/* Badge + іс-қимыл батырмалары */}
                    <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 bg-[#147F26] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                            </svg>
                            {t('dealerBadge') || 'Дилерлік'}
                        </span>
                        <div className="flex items-center gap-2">
                            <button onClick={handleShare} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 bg-white border-solid cursor-pointer">
                                <img src={shareIcon} alt="share" className="w-4 h-4" />
                            </button>
                            <button onClick={handleFavoriteToggle} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 bg-white border-solid cursor-pointer">
                                <img src={isFavorite ? activeHeartIcon : heartIcon} alt="favorite" className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Атауы */}
                    <h1 className="text-2xl font-bold text-gray-900 leading-snug m-0">{item.title}</h1>

                    {/* Рейтинг + қаралым */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Stars value={ratingNum} />
                        <span className="text-sm font-semibold text-gray-700">{String(ratingNum).slice(0, 3)}</span>
                        <span className="text-gray-300">·</span>
                        <button onClick={() => setActiveTab('reviews')} className="text-sm text-[#147F26] hover:underline bg-transparent border-none p-0 cursor-pointer">
                            {item.reviews || 0} {t('feedbacks') || 'пікір'}
                        </button>
                        <span className="text-gray-300">·</span>
                        <span className="text-sm text-gray-400">{item.views || 0} {t('viewsCount') || 'қаралым'}</span>
                    </div>

                    {/* Баға диапазоны */}
                    <div className="bg-[#f6fdf7] rounded-2xl px-5 py-4 border border-[#d1f0d6]">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider m-0 mb-1">
                            {t('from') || 'бастап'}
                        </p>
                        <div className="flex items-end gap-2 flex-wrap">
                            <span className="text-4xl font-extrabold text-[#147F26] leading-none">
                                {minPrice ? fmtPrice(minPrice) : '—'}
                            </span>
                            {item.measurement_unit && (
                                <span className="text-base text-gray-400 mb-0.5">/ {item.measurement_unit}</span>
                            )}
                        </div>
                        {sellers.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2 m-0">
                                {inStockSellers.length} {t('sellers') || 'жеткізуші'} · {sellers.length} {t('total') || 'барлығы'}
                            </p>
                        )}
                    </div>

                    {/* Орын + күні */}
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        <div className="flex items-center gap-1.5">
                            <img src={calendarIcon} alt="" className="w-4 h-4 opacity-50 flex-shrink-0" />
                            <span className="text-sm text-gray-500">{formattedDate}</span>
                        </div>
                    </div>

                    {/* CTA: жеткізушілер тізіміне өту */}
                    <button
                        onClick={scrollToSellers}
                        className="w-full h-14 bg-[#147F26] hover:bg-[#0f6820] text-white font-bold text-base rounded-2xl flex items-center justify-center gap-2.5 transition-colors shadow-lg shadow-[#147F26]/20 border-none cursor-pointer"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('chooseDistributor') || 'Жеткізушіні таңдаңыз'}
                        {inStockSellers.length > 0 && (
                            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {inStockSellers.length}
                            </span>
                        )}
                    </button>

                    {/* Ескерту */}
                    <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-amber-800 m-0">{t('orderViaAppOnly')}</p>
                            <p className="text-xs text-amber-700 mt-1 m-0 leading-relaxed">{t('orderViaAppDesc')}</p>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Табтар бөлімі ────────────────────────────────── */}
            <div className="mt-8 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-100">
                    {tabs.map((tab) => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 py-4 text-sm font-semibold transition-colors relative border-none cursor-pointer bg-white ${activeTab === tab.key ? 'text-[#147F26]' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {tab.label}
                            {activeTab === tab.key && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#147F26] rounded-full" />}
                        </button>
                    ))}
                </div>
                <div className="p-6">
                    {activeTab === 'description' && (
                        <p className="text-gray-700 text-sm leading-relaxed m-0 whitespace-pre-wrap">
                            {item.description || t('noDescription') || 'Сипаттама жоқ'}
                        </p>
                    )}
                    {activeTab === 'specs' && (
                        Array.isArray(item.specifications) && item.specifications.length > 0 ? (
                            <table className="w-full text-sm border-collapse">
                                <tbody>
                                    {item.specifications.map((spec, i) => (
                                        <tr key={i} className="border-b border-gray-50 last:border-0">
                                            <td className={`py-3 px-4 text-gray-500 w-1/2 ${i % 2 === 0 ? 'bg-gray-50/60' : ''}`}>{spec.label}</td>
                                            <td className={`py-3 px-4 text-gray-900 font-semibold ${i % 2 === 0 ? 'bg-gray-50/60' : ''}`}>{spec.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-center text-gray-400 text-sm py-8 m-0">{t('noSpecifications') || 'Сипаттамалар жоқ'}</p>
                        )
                    )}
                    {activeTab === 'reviews' && (
                        <div className="flex flex-col items-center gap-3 py-6">
                            <span className="text-5xl font-extrabold text-gray-900">{String(ratingNum).slice(0, 3)}</span>
                            <Stars value={ratingNum} size="lg" />
                            <span className="text-sm text-gray-400">{item.reviews || 0} {t('feedbacks') || 'пікір'}</span>
                            {!item.reviews && <p className="text-gray-400 text-sm mt-2 m-0">{t('noReviews') || 'Пікірлер жоқ'}</p>}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Жеткізушілер тізімі ──────────────────────────── */}
            {sellers.length > 0 && (
                <div ref={sellersRef} className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-gray-900 m-0">
                            {t('sellers') || 'Жеткізушілер'}
                            <span className="ml-2 text-sm font-normal text-gray-400">({sellers.length})</span>
                        </h2>
                    </div>

                    <div className="flex flex-col gap-3">
                        {sellers.map((seller) => (
                            <SellerRow
                                key={seller.id}
                                seller={seller}
                                measurementUnit={item.measurement_unit}
                                onOrder={(s) => setOrderSeller(s)}
                                onMessage={handleMessageToSeller}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Төменгі метадерек ────────────────────────────── */}
            <div className="mt-6 flex justify-between text-xs text-gray-400 px-1">
                <span>ID: {item.id}</span>
                <span>{item.views || 0} {t('viewsCount') || 'қаралым'}</span>
            </div>

            {/* Тапсырыс беру модалі */}
            {orderSeller && (
                <OrderModal
                    seller={orderSeller}
                    measurementUnit={item.measurement_unit}
                    onClose={() => setOrderSeller(null)}
                />
            )}

        </div>
    );
}

export default DealerPage;
